import {
  Card,
  Col,
  Progress,
  Row,
  Skeleton,
  Statistic,
  Table,
  Tag,
} from 'antd';
import { PageShell, RefreshButton } from '../components/page-shell.js';
import type { Overview } from '../types.js';
import { useAdminQuery } from '../use-admin-query.js';

export function DashboardPage() {
  const query = useAdminQuery<Overview>('/admin/v1/overview');
  const overview = query.data;
  return (
    <PageShell
      title="AI operations overview"
      description="Last 24 hours across Schooltin, Qualeafy, and Imagitin. Monetary values are provider cost, not revenue."
      action={
        <RefreshButton
          loading={query.loading}
          onClick={() => void query.refresh()}
        />
      }
      error={query.error}
    >
      {!overview ? (
        <Skeleton active />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Metric title="Provider calls" value={overview.usage._count._all} />
            <Metric
              title="Provider cost"
              value={microUsd(overview.usage._sum.costMicrousd)}
            />
            <Metric title="Queue depth" value={overview.queueDepth} />
            <Metric
              title="Validation failures"
              value={overview.validationFailures}
            />
          </Row>
          <Card title="Job outcomes" className="section-card">
            <Row gutter={[12, 12]}>
              {Object.entries(overview.jobs).map(([status, count]) => (
                <Col key={status} xs={12} md={6} xl={4}>
                  <Statistic
                    title={status.replaceAll('_', ' ')}
                    value={count}
                  />
                </Col>
              ))}
            </Row>
          </Card>
          <Card title="Budget status" className="section-card">
            <Table
              rowKey="id"
              pagination={false}
              dataSource={overview.budgets}
              columns={[
                { title: 'Scope', dataIndex: 'scopeKey' },
                {
                  title: 'Product',
                  dataIndex: 'productKey',
                  render: textOrDash,
                },
                {
                  title: 'Spend',
                  render: (_, row) =>
                    `${microUsd(row.spentMicrousd)} / ${microUsd(row.amountMicrousd)}`,
                },
                {
                  title: 'Used',
                  render: (_, row) => (
                    <Progress
                      percent={Math.min(100, row.percent)}
                      status={row.warning ? 'exception' : 'normal'}
                      size="small"
                    />
                  ),
                },
                {
                  title: 'Hard cap',
                  render: (_, row) => (
                    <Tag color={row.hardCapEnabled ? 'red' : 'default'}>
                      {row.hardCapEnabled ? 'Enabled' : 'Disabled'}
                    </Tag>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}
    </PageShell>
  );
}

function Metric({ title, value }: { title: string; value: number | string }) {
  return (
    <Col xs={24} sm={12} xl={6}>
      <Card>
        <Statistic title={title} value={value} />
      </Card>
    </Col>
  );
}

export function microUsd(value: string | undefined): string {
  const amount = Number(value ?? '0') / 1_000_000;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: amount < 1 ? 4 : 2,
    maximumFractionDigits: amount < 1 ? 6 : 2,
  }).format(amount);
}

function textOrDash(value: unknown): string {
  return typeof value === 'string' && value ? value : '—';
}
